import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import useWarehouseInventory from '../hooks/useWarehouseInventory';
import api from '../services/api';
import WarehouseHeader from '../components/warehouse/WarehouseHeader';
import InventoryTable from '../components/warehouse/InventoryTable';
import InventoryFilters from '../components/warehouse/InventoryFilters';
import InterWarehouseTransfer from '../components/warehouse/InterWarehouseTransfer';
import ExternalReceipt from '../components/warehouse/ExternalReceipt';
import InternalReceipt from '../components/warehouse/InternalReceipt';
import InternalIssue from '../components/warehouse/InternalIssue';
import ExternalIssue from '../components/warehouse/ExternalIssue';
import Inventory from '../components/warehouse/Inventory';
import ProductHistoryModal from '../components/ProductHistoryModal';

const tabContentStyle = {
  backgroundColor: 'var(--card-bg)', borderRadius: '4px',
  padding: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  marginBottom: '0.75rem'
};

const statCard = (borderColor) => ({
  backgroundColor: 'var(--card-bg)', borderLeft: `3px solid ${borderColor}`,
  borderRadius: '4px', padding: '0.5rem 0.75rem',
  textAlign: 'center', minWidth: '100px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
});

const WarehousePage = () => {
  const { selectedLocation, locationId } = useLocation();
  const { hasPermission } = usePermissions();
  const currentLocationId = locationId;

  // Uprawnienia magazynowe
  const permissions = {
    canAccessWarehouse: hasPermission('magazyn', 'dostep', locationId),
    canViewStock: hasPermission('magazyn', 'podglad', locationId),
    canReceive: hasPermission('magazyn', 'przyjecia', locationId),
    canIssue: hasPermission('magazyn', 'wydania', locationId),
    canTransfer: hasPermission('magazyn', 'przesuniecia', locationId),
    canInventory: hasPermission('magazyn', 'inwentaryzacja', locationId),
    canEditOperations: hasPermission('magazyn', 'edycja', locationId),
    canDeleteOperations: hasPermission('magazyn', 'usuwanie', locationId)
  };

  // Hook zarządzający danymi
  const {
    inventory, categories, stats, availableWarehouses, selectedWarehouse,
    loading, error, activeTab, inventoryView, successMessage, showSuccessAlert,
    setActiveTab, setInventoryView, setSelectedWarehouse, setError,
    setShowSuccessAlert, setSuccessMessage,
    loadInitialData, handleFilter, loadLowStockProducts, handleSaveProduct,
    filtersRef
  } = useWarehouseInventory(currentLocationId);

  // Braki magazynowe
  const [shortages, setShortages] = useState([]);
  const [shortagesCount, setShortagesCount] = useState(0);
  const [shortagesLoading, setShortagesLoading] = useState(false);

  // Ujemne stany
  const [negativeStock, setNegativeStock] = useState([]);
  const [negativeStockCount, setNegativeStockCount] = useState(0);

  // Odroczone WZ (transakcje POS z wz_status='deferred')
  const [deferredWz, setDeferredWz] = useState([]);
  const [deferredWzCount, setDeferredWzCount] = useState(0);
  const [deferredWzLoading, setDeferredWzLoading] = useState(false);
  const [deferredWzRowBusy, setDeferredWzRowBusy] = useState(null);  // id transakcji podczas processing

  // Modal podgladu paragonu (pozycje transakcji + oznaczenie brakow)
  const [previewTx, setPreviewTx] = useState(null);  // { transakcja, pozycje, ... }
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadShortagesCount = useCallback(async () => {
    try {
      const res = await api.get('/warehouse/stock-shortages/count', { params: { location_id: currentLocationId } });
      setShortagesCount(res.data?.data?.count ?? 0);
    } catch (e) { /* ignore */ }
  }, [currentLocationId]);

  const loadShortages = useCallback(async () => {
    setShortagesLoading(true);
    try {
      const res = await api.get('/warehouse/stock-shortages', { params: { location_id: currentLocationId } });
      const items = res.data?.data || [];
      setShortages(items);
      setShortagesCount(items.length);
    } catch (e) {
      console.error('Błąd ładowania braków:', e);
      setError('Błąd ładowania braków paragonowych');
    } finally {
      setShortagesLoading(false);
    }
  }, [currentLocationId]);

  const resolveAllShortages = useCallback(async () => {
    try {
      setShortagesLoading(true);
      const res = await api.post(`/warehouse/stock-shortages/resolve-all`, { location_id: currentLocationId });
      const resolved = res.data?.data?.resolved ?? 0;
      setSuccessMessage(`Rozwiązano ${resolved} braków magazynowych`);
      setShowSuccessAlert(true);
      await loadShortages();
      await loadShortagesCount();
    } catch (e) {
      setError('Błąd rozwiązywania braków: ' + (e.message || ''));
    } finally {
      setShortagesLoading(false);
    }
  }, [currentLocationId, setSuccessMessage, setShowSuccessAlert, setError, loadShortages]);

  const loadNegativeStock = useCallback(async () => {
    setShortagesLoading(true);
    try {
      const res = await api.get('/warehouse/negative-stock', { params: { location_id: currentLocationId } });
      const items = res.data?.data || [];
      setNegativeStock(items);
      setNegativeStockCount(items.length);
    } catch (e) {
      console.error('Błąd ładowania ujemnych stanów:', e);
    } finally {
      setShortagesLoading(false);
    }
  }, [currentLocationId]);

  const loadNegativeStockCount = useCallback(async () => {
    try {
      const res = await api.get('/warehouse/negative-stock/count', { params: { location_id: currentLocationId } });
      setNegativeStockCount(res.data?.data?.count ?? 0);
    } catch (e) { /* ignore */ }
  }, [currentLocationId]);

  const loadDeferredWz = useCallback(async () => {
    setDeferredWzLoading(true);
    try {
      const res = await api.get('/warehouse/deferred-wz/list', { params: { location_id: currentLocationId, limit: 200 } });
      const items = res.data?.data?.items || [];
      setDeferredWz(items);
      setDeferredWzCount(res.data?.data?.total ?? items.length);
    } catch (e) {
      console.error('Blad ladowania odroczonych WZ:', e);
      setError('Blad ladowania odroczonych WZ');
    } finally {
      setDeferredWzLoading(false);
    }
  }, [currentLocationId, setError]);

  const loadDeferredWzCount = useCallback(async () => {
    try {
      const res = await api.get('/warehouse/deferred-wz/list', { params: { location_id: currentLocationId, limit: 1 } });
      setDeferredWzCount(res.data?.data?.total ?? 0);
    } catch (e) { /* ignore */ }
  }, [currentLocationId]);

  const openPreview = useCallback(async (transakcjaId) => {
    try {
      setPreviewLoading(true);
      setPreviewTx({ loading: true, id: transakcjaId });
      const res = await api.get(`/warehouse/deferred-wz/details/${transakcjaId}`);
      setPreviewTx(res.data?.data || null);
    } catch (e) {
      setError(`Blad pobierania paragonu: ${e.response?.data?.error || e.message}`);
      setPreviewTx(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [setError]);

  const closePreview = useCallback(() => setPreviewTx(null), []);

  const generateDeferredWzSingle = useCallback(async (transakcjaId) => {
    try {
      setDeferredWzRowBusy(transakcjaId);
      const res = await api.post(`/warehouse/deferred-wz/generate/${transakcjaId}`, {}, { timeout: 60000 });
      const doc = res.data?.data?.document_number;
      // Usun wiersz z listy od razu (zapobiega drugiemu klikniciu) + update licznik
      setDeferredWz(prev => prev.filter(t => t.id !== transakcjaId));
      setDeferredWzCount(c => Math.max(0, c - 1));
      setSuccessMessage(`WZ ${doc || '?'} wygenerowany dla transakcji #${transakcjaId}`);
      setShowSuccessAlert(true);
    } catch (e) {
      setError(`Blad generowania WZ: ${e.response?.data?.error || e.message}`);
    } finally {
      setDeferredWzRowBusy(null);
    }
  }, [setSuccessMessage, setShowSuccessAlert, setError]);

  const processBatchDeferredWz = useCallback(async (limit = 10) => {
    try {
      setDeferredWzLoading(true);
      const res = await api.post('/warehouse/deferred-wz/process-batch',
        { limit, location_id: currentLocationId },
        { timeout: 180000 }
      );
      const d = res.data?.data || {};
      let msg = `Przetworzono ${d.processed ?? 0} — sukces: ${d.success ?? 0}, blad: ${d.failed ?? 0}`;
      if (d.skipped) msg += `, pominieto ${d.skipped} (czekaja na PZ)`;
      msg += `. Pozostalo: ${d.remaining ?? 0}`;
      setSuccessMessage(msg);
      setShowSuccessAlert(true);
      await loadDeferredWz();
    } catch (e) {
      setError(`Blad batch deferred WZ: ${e.response?.data?.error || e.message}`);
    } finally {
      setDeferredWzLoading(false);
    }
  }, [currentLocationId, loadDeferredWz, setSuccessMessage, setShowSuccessAlert, setError]);

  useEffect(() => { loadShortagesCount(); loadNegativeStockCount(); loadDeferredWzCount(); }, [loadShortagesCount, loadNegativeStockCount, loadDeferredWzCount]);

  // Modal historii produktu (UI-only state)
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);

  const handleShowHistory = (product) => {
    setSelectedProductForHistory(product);
    setShowHistoryModal(true);
  };

  // Brak dostępu
  if (!permissions.canAccessWarehouse && !permissions.canViewStock) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ backgroundColor: 'var(--warning-light)', color: 'var(--warning)', padding: '1.5rem', borderRadius: '8px', border: '1px solid var(--warning)', maxWidth: '500px', margin: '2rem auto' }}>
          <h5 style={{ marginBottom: '0.5rem' }}>🔒 Brak dostępu</h5>
          <p style={{ margin: 0, fontSize: '13px' }}>Nie masz uprawnień do modułu Magazyn. Skontaktuj się z administratorem.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="warehouse-page" style={{ padding: '0.75rem', fontSize: '12px' }}>
      <WarehouseHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loading={loading}
        selectedLocation={selectedLocation}
        permissions={permissions}
        onRefresh={loadInitialData}
      />

      {/* Ostrzeżenie gdy nie wybrano lokalizacji */}
      {!currentLocationId && (
        <div style={{ backgroundColor: 'var(--warning-light)', color: 'var(--warning)', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', border: '1px solid var(--warning)', textAlign: 'center', fontSize: '11px', fontWeight: '600' }}>
          ⚠️ Wybierz lokalizację w prawym górnym rogu!
        </div>
      )}

      {/* Zakładki dokumentów */}
      {activeTab === 'external-receipt' && <div style={tabContentStyle}><ExternalReceipt /></div>}
      {activeTab === 'internal-receipt' && <div style={tabContentStyle}><InternalReceipt /></div>}
      {activeTab === 'internal-issue' && <div style={tabContentStyle}><InternalIssue /></div>}
      {activeTab === 'external-issue' && <div style={tabContentStyle}><ExternalIssue /></div>}
      {activeTab === 'inventory-check' && <div style={tabContentStyle}><Inventory /></div>}
      {activeTab === 'transfers' && <div style={tabContentStyle}><InterWarehouseTransfer /></div>}

      {/* Zakładka inwentarza */}
      {activeTab === 'inventory' && (
        <div>
          {loading && (
            <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: 'var(--card-bg)', borderRadius: '4px', marginBottom: '0.75rem' }}>
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Ładowanie...</span></div>
              <p style={{ marginTop: '0.5rem', color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie danych magazynu...</p>
            </div>
          )}

          {/* Statystyki */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <div style={statCard('var(--purple)')}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--purple)' }}>{stats.total_products ?? 0}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Produkty</div>
            </div>
            <div style={statCard('var(--success)')}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--success)' }}>{stats.in_stock ?? 0}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Dostępne</div>
            </div>
            <div style={{ ...statCard('var(--danger)'), cursor: 'pointer' }} onClick={() => { setInventoryView('shortages'); loadShortages(); }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--danger)' }}>{shortagesCount}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Braki</div>
            </div>
            <div style={{ ...statCard('var(--info)'), minWidth: '280px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '600' }}>Widok</div>
              <div style={{ display: 'flex', gap: '3px' }}>
                {[
                  { key: 'available', label: 'Dostępne', color: 'var(--success)', action: () => handleFilter({ ...filtersRef.current, available_only: true, stock_zero: false }, 1) },
                  { key: 'all', label: 'Wszystkie', color: 'var(--cyan)', action: () => handleFilter({ ...filtersRef.current, available_only: false, stock_zero: false }, 1) },
                  { key: 'unavailable', label: 'Niedostępne', color: 'var(--danger)', action: () => handleFilter({ ...filtersRef.current, available_only: false, stock_zero: true }, 1) },
                  { key: 'negative-stock', label: 'Ujemne stany', color: 'var(--danger)', action: () => loadNegativeStock() },
                  { key: 'shortages', label: 'Braki paragonów', color: 'var(--warning)', action: () => loadShortages() },
                  { key: 'deferred-wz', label: `Odroczone WZ${deferredWzCount ? ` (${deferredWzCount})` : ''}`, color: 'var(--purple)', action: () => loadDeferredWz() },
                ].map(btn => {
                  const isActive = inventoryView === btn.key;
                  return (
                    <button
                      key={btn.key}
                      style={{
                        padding: '4px 10px', fontSize: '11px',
                        border: isActive ? `2px solid ${btn.color}` : '1px solid var(--border)',
                        backgroundColor: isActive ? btn.color : 'var(--card-bg)',
                        color: isActive ? '#fff' : 'var(--text-secondary)',
                        fontWeight: isActive ? '700' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => { setInventoryView(btn.key); btn.action(); }}
                    >{btn.label}</button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Komunikaty */}
          {error && (
            <div style={{ backgroundColor: 'var(--danger-light)', color: 'var(--danger)', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', border: '1px solid var(--danger-light)', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>⚠️ {error}</span>
              <button style={{ background: 'none',  color: 'var(--danger)', fontSize: '14px' }} onClick={() => setError('')}>×</button>
            </div>
          )}
          {showSuccessAlert && (
            <div style={{ backgroundColor: 'var(--success-light)', color: 'var(--success)', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', border: '1px solid var(--success-light)', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>✅ {successMessage}</span>
              <button style={{ background: 'none',  color: 'var(--success)', fontSize: '14px' }} onClick={() => { setShowSuccessAlert(false); setSuccessMessage(''); }}>×</button>
            </div>
          )}

          {/* Selektor magazynu */}
          {availableWarehouses.length > 1 && (
            <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>🏪 Magazyn:</span>
              <select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', minWidth: '150px' }}
              >
                <option value="all">Wszystkie ({availableWarehouses.length})</option>
                {availableWarehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.nazwa} ({w.kod_magazynu})</option>
                ))}
              </select>
              {selectedWarehouse !== 'all' && (
                <span style={{ fontSize: '10px', color: 'var(--info)', backgroundColor: 'var(--info-light)', padding: '0.2rem 0.5rem', borderRadius: '3px' }}>Filtrowanie aktywne</span>
              )}
            </div>
          )}

          {/* Widok braków magazynowych */}
          {inventoryView === 'shortages' && (
            <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: 'var(--danger)', marginRight: '6px' }}></i>
                  Braki magazynowe ({shortages.length})
                </h6>
                <button
                  onClick={resolveAllShortages}
                  disabled={shortagesLoading || shortages.length === 0}
                  style={{
                    padding: '6px 12px', fontSize: '12px',
                    border: '1px solid var(--success)', color: 'var(--success)',
                    backgroundColor: 'var(--card-bg)', cursor: 'pointer',
                    fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  <i className="fas fa-check-double"></i>
                  Rozwiąż automatycznie
                </button>
              </div>
              {shortagesLoading ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Ładowanie...</div>
              ) : shortages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--success)', fontSize: '12px' }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '24px', display: 'block', marginBottom: '0.5rem' }}></i>
                  Brak nierozwiązanych braków magazynowych
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                      <tr>
                        {['LP', 'Produkt', 'Paragon', 'Sprzedano', 'Brakowało', 'Aktualny stan', 'Status', 'Data'].map(h => (
                          <th key={h} style={{ padding: '0.4rem 0.5rem', borderBottom: '2px solid var(--border)', textAlign: h === 'LP' ? 'center' : 'left', fontWeight: '600', fontSize: '10px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shortages.map((s, idx) => (
                        <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.3rem 0.5rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)' }}>{idx + 1}</td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <strong>{s.nazwa_produktu || s.product_name || '-'}</strong>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{s.numer_paragonu || '-'}</code>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>{s.ilosc_sprzedana ?? '-'}</td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <span style={{ color: 'var(--danger)', fontWeight: '600' }}>{s.ilosc_brakujaca ?? '-'}</span>
                            {Number(s.ilosc_pokryta) > 0 && (
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '4px' }}>
                                (pokryto {Number(s.ilosc_pokryta)})
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <span style={{
                              fontSize: '10px', padding: '2px 6px', borderRadius: '3px', color: '#fff',
                              backgroundColor: (s.stan_aktualny ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)'
                            }}>
                              {s.stan_aktualny ?? '?'}
                            </span>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <span style={{
                              fontSize: '9px', padding: '2px 6px', borderRadius: '3px',
                              backgroundColor: s.status === 'pending' ? 'var(--warning)' : 'var(--success)',
                              color: '#fff', fontWeight: '600'
                            }}>
                              {s.status === 'pending' ? 'Oczekujący' : 'Rozwiązany'}
                            </span>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem', fontSize: '10px', color: 'var(--text-muted)' }}>
                            {s.created_at ? new Date(s.created_at).toLocaleDateString('pl-PL') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Widok ujemnych stanów */}
          {inventoryView === 'negative-stock' && (
            <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>
                  <i className="fas fa-minus-circle" style={{ color: 'var(--danger)', marginRight: '6px' }}></i>
                  Produkty z ujemnym stanem ({negativeStock.length})
                </h6>
              </div>
              {shortagesLoading ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Ładowanie...</div>
              ) : negativeStock.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--success)', fontSize: '12px' }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '24px', display: 'block', marginBottom: '0.5rem' }}></i>
                  Brak produktów z ujemnym stanem
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                      <tr>
                        {['LP', 'Produkt', 'Kod / EAN', 'Stan aktualny', 'Lokalizacja'].map(h => (
                          <th key={h} style={{ padding: '0.4rem 0.5rem', borderBottom: '2px solid var(--border)', textAlign: h === 'LP' ? 'center' : 'left', fontWeight: '600', fontSize: '10px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {negativeStock.map((item, idx) => (
                        <tr key={item.id || idx} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.3rem 0.5rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)' }}>{idx + 1}</td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <strong>{item.nazwa || '-'}</strong>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{item.ean || item.kod_produktu || '-'}</code>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--danger)', padding: '2px 8px', backgroundColor: 'var(--danger-light)', borderRadius: '3px' }}>
                              {item.stan_aktualny}
                            </span>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem', fontSize: '10px', color: 'var(--text-muted)' }}>
                            {item.lokalizacja || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Widok odroczonych WZ (transakcje POS z wz_status='deferred') */}
          {inventoryView === 'deferred-wz' && (
            <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>
                  <i className="fas fa-file-invoice" style={{ color: '#6610f2', marginRight: '6px' }}></i>
                  Odroczone WZ — transakcje POS czekajace na wygenerowanie ({deferredWzCount})
                </h6>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => processBatchDeferredWz(10)}
                    disabled={deferredWzLoading || deferredWz.length === 0}
                    style={{
                      padding: '6px 12px', fontSize: '12px',
                      border: '1px solid var(--purple)', color: deferredWzLoading ? 'var(--text-muted)' : 'var(--purple)',
                      backgroundColor: 'var(--card-bg)', cursor: deferredWzLoading ? 'wait' : 'pointer',
                      fontWeight: '600',
                    }}
                  >
                    <i className="fas fa-play" style={{ marginRight: '4px' }}></i>
                    Wygeneruj 10
                  </button>
                  <button
                    onClick={() => processBatchDeferredWz(50)}
                    disabled={deferredWzLoading || deferredWz.length === 0}
                    style={{
                      padding: '6px 12px', fontSize: '12px',
                      border: '1px solid var(--purple)', color: '#fff',
                      backgroundColor: deferredWzLoading ? 'var(--text-muted)' : 'var(--purple)',
                      cursor: deferredWzLoading ? 'wait' : 'pointer',
                      fontWeight: '600',
                    }}
                  >
                    <i className="fas fa-forward" style={{ marginRight: '4px' }}></i>
                    Wygeneruj 50
                  </button>
                  <button
                    onClick={loadDeferredWz}
                    disabled={deferredWzLoading}
                    title="Odswiez"
                    style={{
                      padding: '6px 12px', fontSize: '12px',
                      border: '1px solid var(--border)', color: 'var(--text-secondary)',
                      backgroundColor: 'var(--card-bg)', cursor: 'pointer',
                    }}
                  >
                    <i className="fas fa-sync"></i>
                  </button>
                </div>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '0.5rem', padding: '0.35rem', backgroundColor: 'var(--info-light)', borderLeft: '3px solid var(--info)', borderRadius: '3px' }}>
                <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                Transakcje POS realizowane przy braku stanu magazynowego. WZ generuje sie <b>tylko gdy braki sa rozwiazane</b> (po przyjeciu PZ).
                Transakcje z oczekujacymi brakami sa pominiete - czekaja na uzupelnienie stanu.
              </div>
              {deferredWzLoading ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Ladowanie...</div>
              ) : deferredWz.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--success)', fontSize: '12px' }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '24px', display: 'block', marginBottom: '0.5rem' }}></i>
                  Brak transakcji POS z odroczonym WZ
                </div>
              ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                      <tr>
                        {['Data', 'Paragon', 'Klient', 'Kwota', 'Lokalizacja', 'Braki', 'Akcja'].map(h => (
                          <th key={h} style={{ padding: '0.4rem 0.5rem', borderBottom: '2px solid var(--border)', textAlign: 'left', fontWeight: '600', fontSize: '10px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {deferredWz.map((t) => (
                        <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.3rem 0.5rem', fontSize: '10px', color: 'var(--text-muted)' }}>
                            {t.data_transakcji ? new Date(t.data_transakcji).toLocaleDateString('pl-PL') : '-'}
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            <code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{t.numer_paragonu || `#${t.id}`}</code>
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>{(t.klient_nazwa || '').trim() || 'Klient detaliczny'}</td>
                          <td style={{ padding: '0.3rem 0.5rem', fontWeight: '600' }}>{Number(t.suma_brutto || 0).toFixed(2)} zl</td>
                          <td style={{ padding: '0.3rem 0.5rem', fontSize: '10px', color: 'var(--text-muted)' }}>{t.location_name || t.location_id || '-'}</td>
                          <td style={{ padding: '0.3rem 0.5rem' }}>
                            {t.pending_shortages > 0 ? (
                              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '3px', backgroundColor: 'var(--warning)', color: '#fff', fontWeight: '600' }}>
                                {t.pending_shortages} pending
                              </span>
                            ) : (
                              <span style={{ fontSize: '10px', color: 'var(--success)' }}>
                                <i className="fas fa-check"></i> rozwiazane
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '0.3rem 0.5rem', whiteSpace: 'nowrap' }}>
                            <button
                              onClick={() => openPreview(t.id)}
                              disabled={deferredWzLoading}
                              title="Podglad paragonu (pozycje + ktore maja braki)"
                              style={{
                                padding: '4px 10px', fontSize: '11px',
                                border: '1px solid var(--info)', color: 'var(--info)',
                                backgroundColor: 'var(--card-bg)', cursor: 'pointer',
                                fontWeight: '600', marginRight: '4px',
                              }}
                            >
                              <i className="fas fa-eye"></i> Podglad
                            </button>
                            {t.pending_shortages > 0 ? (
                              <span
                                title={`Transakcja ma ${t.pending_shortages} nierozwiazanych brakow — czeka na PZ ktore uzupelni stan`}
                                style={{
                                  fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic',
                                  padding: '0.2rem 0.5rem', display: 'inline-block'
                                }}
                              >
                                <i className="fas fa-clock" style={{ marginRight: '3px' }}></i>
                                czeka na PZ
                              </span>
                            ) : (
                              <button
                                onClick={() => generateDeferredWzSingle(t.id)}
                                disabled={deferredWzRowBusy === t.id || deferredWzLoading}
                                style={{
                                  padding: '4px 10px', fontSize: '11px',
                                  border: '1px solid var(--purple)',
                                  backgroundColor: deferredWzRowBusy === t.id ? 'var(--border)' : 'var(--card-bg)',
                                  color: 'var(--purple)', cursor: deferredWzRowBusy === t.id ? 'wait' : 'pointer',
                                  fontWeight: '600',
                                }}
                              >
                                {deferredWzRowBusy === t.id ? (
                                  <><i className="fas fa-spinner fa-spin"></i> generuje...</>
                                ) : (
                                  <><i className="fas fa-play"></i> Wygeneruj WZ</>
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {inventoryView !== 'shortages' && inventoryView !== 'negative-stock' && inventoryView !== 'deferred-wz' && <InventoryFilters onFilter={handleFilter} categories={categories} loading={loading} totalProducts={inventory.pagination?.total || 0} />}

          {inventoryView !== 'shortages' && inventoryView !== 'negative-stock' && inventoryView !== 'deferred-wz' && <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <InventoryTable
              products={Array.isArray(inventory.products) ? inventory.products : []}
              loading={loading}
              onEdit={(product) => {}}
              onDelete={(productId) => {}}
              onUpdateStock={(productId, newStock) => {}}
              onSaveProduct={handleSaveProduct}
              onShowHistory={handleShowHistory}
              pagination={inventory.pagination}
              onPageChange={(page) => handleFilter(filtersRef.current, page)}
              currentView={inventoryView}
              canEdit={permissions.canEditOperations}
            />
          </div>}
        </div>
      )}

      {/* Zakładka statystyk */}
      {activeTab === 'stats' && (
        <div style={tabContentStyle}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={statCard('var(--purple)')}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--purple)' }}>{stats.total_products ?? 0}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Łącznie produktów</div>
            </div>
            <div style={statCard('var(--success)')}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--success)' }}>{stats.in_stock ?? 0}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Dostępne</div>
            </div>
            <div style={statCard('var(--danger)')}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--danger)' }}>{stats.out_of_stock ?? 0}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Niedostępne</div>
            </div>
            <div style={statCard('var(--success)')} title="Suma partii FIFO: ilość pozostała × cena zakupu netto; tylko aktywne towary, kaucje osobno">
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--success)' }}>
                {(stats.wartosc_wg_stanu_netto ?? stats.wartosc_magazynu_netto) == null ? '—' : `${Number(stats.wartosc_wg_stanu_netto ?? stats.wartosc_magazynu_netto).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Wartość magazynu netto (stan × cena zakupu)</div>
              {stats.wartosc_wg_stanu_netto != null && stats.wartosc_magazynu_netto != null && Math.abs(stats.wartosc_wg_stanu_netto - stats.wartosc_magazynu_netto) >= 0.01 && (
                <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>wg partii FIFO: {Number(stats.wartosc_magazynu_netto).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł</div>
              )}
              {stats.kaucje_szt > 0 && (
                <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>opakowania/kaucje: {Math.round(stats.kaucje_szt)} szt (poza wartością)</div>
              )}
            </div>
            <div style={statCard('var(--warning)')} title="Stan × cena sprzedaży brutto – to NIE jest wartość magazynu">
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--warning)' }}>{Number(stats.total_sell_value_brutto ?? stats.total_value ?? 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Wartość sprzedaży brutto</div>
            </div>
            <div style={statCard('var(--info)')}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--info)' }}>{(stats.avg_price ?? 0).toFixed(2)} zł</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Średnia cena</div>
            </div>
          </div>
        </div>
      )}

      {/* Modal historii produktu */}
      {showHistoryModal && selectedProductForHistory && (
        <ProductHistoryModal
          productId={selectedProductForHistory.id}
          productName={selectedProductForHistory.nazwa || selectedProductForHistory.name}
          isOpen={showHistoryModal}
          onClose={() => { setShowHistoryModal(false); setSelectedProductForHistory(null); }}
        />
      )}

      {/* Modal podgladu paragonu dla odroczonego WZ */}
      {previewTx && (
        <div
          onClick={closePreview}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--card-bg)', borderRadius: '6px', padding: '1rem',
              maxWidth: '900px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
              <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>
                <i className="fas fa-receipt" style={{ marginRight: '6px', color: 'var(--info)' }}></i>
                Podglad paragonu
                {previewTx.transakcja?.numer_paragonu && (
                  <code style={{ fontSize: '12px', marginLeft: '8px', backgroundColor: 'var(--border)', padding: '2px 6px', borderRadius: '3px' }}>
                    {previewTx.transakcja.numer_paragonu}
                  </code>
                )}
              </h5>
              <button
                onClick={closePreview}
                style={{
                  background: 'none', border: 'none', fontSize: '20px',
                  color: 'var(--text-muted)', cursor: 'pointer', padding: '0 0.25rem',
                }}
              >×</button>
            </div>

            {previewLoading || previewTx.loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
                <div style={{ marginTop: '0.5rem', fontSize: '12px' }}>Ladowanie...</div>
              </div>
            ) : !previewTx.transakcja ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--danger)' }}>
                Nie udalo sie pobrac szczegolow transakcji
              </div>
            ) : (
              <>
                {/* Naglowek transakcji */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '0.5rem', marginBottom: '0.75rem', fontSize: '11px',
                  backgroundColor: 'var(--bg)', padding: '0.5rem', borderRadius: '4px',
                }}>
                  <div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>Data</div>
                    <div style={{ fontWeight: '600' }}>
                      {previewTx.transakcja.data_transakcji ? new Date(previewTx.transakcja.data_transakcji).toLocaleString('pl-PL') : '-'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>Kasjer</div>
                    <div style={{ fontWeight: '600' }}>{previewTx.transakcja.kasjer_login || '-'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>Klient</div>
                    <div style={{ fontWeight: '600' }}>{(previewTx.transakcja.klient_nazwa || '').trim() || 'Klient detaliczny'}</div>
                    {previewTx.transakcja.klient_nip && (
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>NIP: {previewTx.transakcja.klient_nip}</div>
                    )}
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>Lokalizacja</div>
                    <div style={{ fontWeight: '600' }}>{previewTx.transakcja.location_name || previewTx.transakcja.location_id || '-'}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>Suma brutto</div>
                    <div style={{ fontWeight: '700', color: 'var(--success)' }}>
                      {Number(previewTx.transakcja.suma_brutto || 0).toFixed(2)} zl
                    </div>
                  </div>
                </div>

                {/* Podsumowanie brakow */}
                {(previewTx.pending_count > 0 || previewTx.resolved_count > 0) && (
                  <div style={{
                    display: 'flex', gap: '6px', marginBottom: '0.5rem', fontSize: '10px',
                  }}>
                    {previewTx.pending_count > 0 && (
                      <span style={{
                        padding: '3px 8px', borderRadius: '3px', backgroundColor: 'var(--danger)',
                        color: '#fff', fontWeight: '600',
                      }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: '3px' }}></i>
                        {previewTx.pending_count} brak(ow) oczekujacych
                      </span>
                    )}
                    {previewTx.resolved_count > 0 && (
                      <span style={{
                        padding: '3px 8px', borderRadius: '3px', backgroundColor: 'var(--success)',
                        color: '#fff', fontWeight: '600',
                      }}>
                        <i className="fas fa-check" style={{ marginRight: '3px' }}></i>
                        {previewTx.resolved_count} rozwiazanych
                      </span>
                    )}
                  </div>
                )}

                {/* Pozycje */}
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: 'var(--bg)' }}>
                    <tr>
                      <th style={{ padding: '0.4rem', textAlign: 'center', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>LP</th>
                      <th style={{ padding: '0.4rem', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Produkt</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Ilosc</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Cena</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Wartosc</th>
                      <th style={{ padding: '0.4rem', textAlign: 'center', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Stan teraz</th>
                      <th style={{ padding: '0.4rem', textAlign: 'center', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Brak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewTx.pozycje || []).map((p, idx) => {
                      const isPending = p.shortage_status === 'pending';
                      const isResolved = p.shortage_status === 'resolved';
                      const hasShortage = isPending || isResolved;
                      // CZERWONE podswietlenie dla brakow oczekujacych (pending)
                      // zielone dla juz rozwiazanych (resolved)
                      return (
                        <tr
                          key={p.id || idx}
                          style={{
                            borderBottom: '1px solid var(--border)',
                            backgroundColor: isPending
                              ? 'rgba(220,53,69,0.12)'   // czerwony - brakuje TERAZ
                              : (isResolved ? 'rgba(40,167,69,0.06)' : 'transparent'),
                            borderLeft: isPending ? '3px solid var(--danger)' : (isResolved ? '3px solid var(--success)' : '3px solid transparent'),
                          }}
                        >
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <div style={{ fontWeight: hasShortage ? '700' : '500', color: isPending ? 'var(--danger)' : 'inherit' }}>
                              {isPending && <i className="fas fa-times-circle" style={{ marginRight: '4px', color: 'var(--danger)' }}></i>}
                              {p.nazwa_produktu || '-'}
                            </div>
                            {p.kod_produktu && (
                              <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                                <code>{p.kod_produktu}</code>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>
                            {Number(p.ilosc || 0).toFixed(p.jednostka === 'kg' ? 3 : 0)}
                            <span style={{ fontSize: '9px', color: 'var(--text-muted)', marginLeft: '3px' }}>{p.jednostka || 'szt'}</span>
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>
                            {Number(p.cena_jednostkowa || 0).toFixed(2)} zl
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', fontWeight: '600' }}>
                            {Number(p.wartosc_brutto || 0).toFixed(2)} zl
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>
                            <span style={{
                              fontSize: '10px', padding: '2px 6px', borderRadius: '3px',
                              backgroundColor: Number(p.current_stock) >= Number(p.ilosc) ? 'var(--success)' : 'var(--danger)',
                              color: '#fff', fontWeight: '600',
                            }}>
                              {Number(p.current_stock || 0).toFixed(p.jednostka === 'kg' ? 2 : 0)}
                            </span>
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>
                            {isPending ? (
                              <span style={{
                                fontSize: '10px', padding: '3px 8px', borderRadius: '3px',
                                backgroundColor: 'var(--danger)', color: '#fff', fontWeight: '700',
                                boxShadow: '0 0 6px rgba(220,53,69,0.4)',
                              }}>
                                <i className="fas fa-exclamation-triangle" style={{ marginRight: '3px' }}></i>
                                BRAK {Number(p.shortage_qty || 0).toFixed(p.jednostka === 'kg' ? 2 : 0)} {p.jednostka || ''}
                              </span>
                            ) : isResolved ? (
                              <span style={{
                                fontSize: '9px', padding: '2px 6px', borderRadius: '3px',
                                backgroundColor: 'var(--success)', color: '#fff', fontWeight: '600',
                              }}>
                                <i className="fas fa-check"></i> uzupelnione
                              </span>
                            ) : (
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Stopka modalu: przycisk generuj + zamknij */}
                <div style={{
                  display: 'flex', gap: '6px', justifyContent: 'flex-end',
                  marginTop: '1rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)',
                }}>
                  <button
                    onClick={closePreview}
                    style={{
                      padding: '6px 12px', fontSize: '12px',
                      border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)',
                      color: 'var(--text-secondary)', cursor: 'pointer',
                    }}
                  >
                    Zamknij
                  </button>
                  {previewTx.pending_count === 0 && previewTx.transakcja?.wz_status === 'deferred' && (
                    <button
                      onClick={async () => {
                        const id = previewTx.transakcja.id;
                        closePreview();
                        await generateDeferredWzSingle(id);
                      }}
                      disabled={deferredWzRowBusy === previewTx.transakcja?.id}
                      style={{
                        padding: '6px 12px', fontSize: '12px',
                        border: '1px solid var(--purple)', backgroundColor: 'var(--purple)',
                        color: '#fff', cursor: 'pointer', fontWeight: '600',
                      }}
                    >
                      <i className="fas fa-play" style={{ marginRight: '4px' }}></i>
                      Wygeneruj WZ
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehousePage;
